import React from "react";

import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import { Button } from "@material-ui/core";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, {
  EditorFieldControl,
  TextFieldControl,
} from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";
import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";
import {
  compose,
  fileSizeValidator,
  imageTypesValidator,
  lengthValidator,
} from "utils/validators";
import { PUBLISH_STATUSES } from "constants/models";
import ROUTES from "navigation/routes";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  PublishStatusWrapper,
  ImagePreviewContainer,
  UploadPreviewFieldControl,
} from "./styles";
import TrainerStore from "stores/TrainerStore";
import { constants } from "../constants";

import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import { Divider } from "../../CoachResources/Coach/CoachDetails/styles";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ id: string }, {}, LocationState> &
  SnackbarContextProps & {};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class TrainerDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewItem() {
    const { id } = this.props.match.params;

    return id === undefined;
  }

  get itemId() {
    const { id } = this.props.match.params;

    const parsedId = Number.parseInt(id);
    return typeof parsedId === "number" ? parsedId : undefined;
  }

  get backClickInfo() {
    return constants.details.backButton;
  }

  get backClickPath() {
    return ROUTES.CONTENT_TRAINERS;
  }

  @computed get displayValues(): any {
    const formData = TrainerStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get initialValues() {
    if (this.isNewItem) {
      return {};
    } else {
      return {
        ...TrainerStore.details?.formData,
      };
    }
  }

  @computed get publishStatus() {
    return TrainerStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    if (this.isNewItem) {
      history.replace({ state: { editMode: true } });
    } else {
      await TrainerStore.fetchDetails(this.itemId);
    }
  }

  componentWillUnmount() {
    TrainerStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewItem) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_TRAINERS_DETAILS, {
      id: this.itemId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_TRAINERS_DETAILS, {
      id: this.itemId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.publishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const { id, title, description, trainingCategories, file } = values;

    const data = {
      title: title?.trim(),
      description: description?.trim(),
      trainingCategories: trainingCategories?.trim(),
    };

    try {
      if (this.isNewItem) {
        const payload = { ...data };
        const id = await TrainerStore.createTrainer(payload);

        if (id) {
          if (file) {
            if (typeof file !== "string") {
              const payload = { file, id };
              await TrainerStore.editTrainerPhoto(payload);
            }
          }

          const path = generatePath(ROUTES.CONTENT_TRAINERS_DETAILS, {
            id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          TrainerStore.fetchDetails(id);
        }
      } else {
        const payload = { ...data, id };
        await TrainerStore.editTrainerInfo(payload);

        if (file) {
          if (typeof file !== "string") {
            const payload = { file, id };
            await TrainerStore.editTrainerPhoto(payload);
          }
        }

        this.exitEditMode();
        TrainerStore.fetchDetails(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={constants.details.title}
          leftControls={
            this.publishStatus && (
              <PublishStatusWrapper>
                <PublishStatus status={this.publishStatus} />
              </PublishStatusWrapper>
            )
          }
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                {this.backClickInfo}
              </Button>
            </BackButtonWrapper>
          }
          isLoading={TrainerStore.isTrainerLoading}
        >
          {this.closeConfirmationIsOpened && (
            <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
              <ConfirmationDialog
                title="Discard Changes"
                text="Are you sure you would like to discard changes? All information will be lost."
                declineText="Cancel"
                acceptText="Discard"
                onDecline={this.closeConfirmationPopup}
                onAccept={this.onAccept}
              />
            </Popup>
          )}
          <FormControl
            onSubmit={this.onSubmit}
            initialValues={this.initialValues}
            keepDirtyOnReinitialize={this.isNewItem}
          >
            {this.updateConfirmationIsOpened && (
              <FormSpy subscription={{ values: true }}>
                {({ values }) => (
                  <Popup
                    onClose={this.closeUpdateConfirmation}
                    withoutExitButton
                  >
                    <ConfirmationDialog
                      title="Save Changes to Production"
                      text="All changes made will be published to production. Are you sure you want to proceed?"
                      acceptText="Proceed"
                      declineText="Cancel"
                      onAccept={this.confirmUpdate(values)}
                      onDecline={this.closeUpdateConfirmation}
                    />
                  </Popup>
                )}
              </FormSpy>
            )}
            <FormSpy
              subscription={{
                dirty: true,
                dirtyFields: true,
                initialValues: true,
                values: true,
              }}
              onChange={debounce(this.onChangeIsPopupDirty, 500)}
            />
            <Container>
              <ControlsContainer>
                {this.isEditMode ? (
                  <>
                    <ControlContainer>
                      <CancelButtonControl
                        render={this.renderCancelButton}
                        onClick={this.onCancelClick}
                      />
                    </ControlContainer>
                    <ControlContainer>
                      <SubmitButtonControl render={this.renderButton} />
                    </ControlContainer>
                  </>
                ) : (
                  !this.isReadOnlyMode && (
                    <ControlContainer>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.onEditClick}
                        fullWidth
                      >
                        Edit
                      </Button>
                    </ControlContainer>
                  )
                )}
              </ControlsContainer>

              <GroupRow>
                <Column width="50%">
                  <Row>
                    {this.isEditMode ? (
                      <UploadPreviewFieldControl
                        variant="outlined"
                        margin="none"
                        name="file"
                        acceptedFiles={ACCEPTED_IMAGE_TYPES}
                        validate={compose(
                          this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                          fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                        )}
                      />
                    ) : (
                      <ImagePreviewContainer
                        image={this.displayValues?.photo}
                      />
                    )}
                  </Row>
                </Column>
                <Column>
                  <Row>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Row>

                  <Row>
                    <Column>
                      {this.isEditMode ? (
                        <TextFieldControl
                          variant="outlined"
                          margin="none"
                          name="trainingCategories"
                          label="Training Categories (separated by commas)"
                          fullWidth
                        />
                      ) : (
                        <SimpleField
                          label="Training Categories (separated by commas)"
                          value={this.displayValues?.trainingCategories}
                        />
                      )}
                    </Column>
                  </Row>
                </Column>
              </GroupRow>

              <Divider />

              <GroupRow>
                <Column>
                  <Row>
                    {this.isEditMode ? (
                      <TextFieldControl
                        name="description"
                        variant="outlined"
                        margin="none"
                        label="Description"
                        rows={5}
                        rowsMax={10}
                        fullWidth
                        multiline
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Description"
                        value={this.displayValues?.description}
                        isRichText
                      />
                    )}
                  </Row>
                </Column>
              </GroupRow>
            </Container>
          </FormControl>
        </Page>
      </LayoutContainer>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={TrainerStore.isTrainerManaging}
      fullWidth
    >
      {this.isNewItem ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(TrainerDetails));
